import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, MessageSquare, Clock, CheckCircle, AlertCircle, User, Users, Flag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ComplaintManagement = () => {
  const { token, user } = useContext(AuthContext);
  
  // Helper to check user roles
  const isFranchiseeRole = ['FRANCHISEE', 'FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR'].includes(user?.role);
  const isHQRole = ['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role);
  const canManageComplaints = ['HQ_ADMIN', 'SYSTEM_ADMIN', 'REGIONAL_COORDINATOR'].includes(user?.role);
  
  const [complaints, setComplaints] = useState([]);
  const [users, setUsers] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  
  const [formData, setFormData] = useState({
    franchisee_id: user.franchisee_id || '',
    category: '',
    priority: 'Medium',
    title: '',
    description: '',
    supporting_docs: []
  });

  const [updateData, setUpdateData] = useState({
    status: '',
    resolution_notes: ''
  });

  const [assignedTo, setAssignedTo] = useState('');

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [complaintsRes, usersRes, franchiseesRes] = await Promise.all([
        axios.get(`${API}/complaints`, { headers: { Authorization: `Bearer ${token}` } }),
        canManageComplaints ? axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: [] }),
        axios.get(`${API}/franchisees`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setComplaints(complaintsRes.data);
      setUsers(usersRes.data);
      setFranchisees(franchiseesRes.data);
    } catch (error) {
      toast.error('Failed to fetch complaints');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get franchisee name from ID
  const getFranchiseeName = (franchiseeId) => {
    const franchisee = franchisees.find(f => f.franchisee_id === franchiseeId);
    return franchisee?.company_name || franchiseeId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/complaints`, {
        ...formData,
        submitted_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket submitted successfully');
      setOpenDialog(false);
      fetchData();
      setFormData({ ...formData, category: '', priority: 'Medium', title: '', description: '', supporting_docs: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/complaints/${selectedComplaint.complaint_id}`, {
        ...updateData,
        updated_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket updated successfully');
      setUpdateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleAssign = async () => {
    if (!assignedTo) return;
    
    try {
      await axios.patch(`${API}/complaints/${selectedComplaint.complaint_id}`, {
        assigned_to: assignedTo,
        status: 'In Progress',
        updated_by: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ticket assigned successfully');
      setAssignDialog(false);
      setAssignedTo('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const openUpdateDialog = (complaint) => {
    setSelectedComplaint(complaint);
    setUpdateData({ status: complaint.status, resolution_notes: complaint.resolution_notes || '' });
    setUpdateDialog(true);
  };

  const openAssignDialog = (complaint) => {
    setSelectedComplaint(complaint);
    setAssignedTo(complaint.assigned_to || '');
    setAssignDialog(true);
  };

  const openViewDialog = (complaint) => {
    setSelectedComplaint(complaint);
    setViewDialog(true);
  };

  const getStatusIcon = (status) => {
    if (status === 'Resolved') return CheckCircle;
    if (status === 'In Progress') return Clock;
    return AlertCircle;
  };

  const getStatusColor = (status) => {
    if (status === 'Resolved') return 'default';
    if (status === 'In Progress') return 'secondary';
    return 'destructive';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'High') return 'text-red-500';
    if (priority === 'Medium') return 'text-yellow-500';
    return 'text-green-500';
  };

  const filterByStatus = (status) => {
    return complaints.filter(c => c.status === status);
  };

  const openTickets = filterByStatus('Open');
  const inProgressTickets = filterByStatus('In Progress');
  const resolvedTickets = filterByStatus('Resolved');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="complaint-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">Customer Service Center</h1>
            <p className="text-muted-foreground mt-1">Ticket management and support system</p>
          </div>
          {isFranchiseeRole && (
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogTrigger asChild>
                <Button data-testid="submit-complaint-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Ticket
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit Support Ticket</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category*</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})} required>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="System">System Issue</SelectItem>
                        <SelectItem value="Training">Training Support</SelectItem>
                        <SelectItem value="Finance">Financial Query</SelectItem>
                        <SelectItem value="Materials">Materials & Supplies</SelectItem>
                        <SelectItem value="Operations">Operations</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Priority*</Label>
                    <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})} required>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="High">High - Urgent</SelectItem>
                        <SelectItem value="Medium">Medium - Normal</SelectItem>
                        <SelectItem value="Low">Low - When Possible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Subject*</Label>
                    <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required data-testid="title-input" placeholder="Brief description of the issue" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Description*</Label>
                    <Textarea rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required data-testid="description-input" placeholder="Please provide detailed information about your issue" />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-complaint-form-btn">Submit Ticket</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-destructive/20 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Open Tickets</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{openTickets.length}</p>
                </div>
                <div className="bg-destructive/10 h-12 w-12 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-secondary/20 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">In Progress</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{inProgressTickets.length}</p>
                </div>
                <div className="bg-secondary/10 h-12 w-12 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Resolved</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{resolvedTickets.length}</p>
                </div>
                <div className="bg-primary/10 h-12 w-12 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tickets Tabs */}
        <Tabs defaultValue="open" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="open">Open ({openTickets.length})</TabsTrigger>
            <TabsTrigger value="progress">In Progress ({inProgressTickets.length})</TabsTrigger>
            <TabsTrigger value="resolved">Resolved ({resolvedTickets.length})</TabsTrigger>
          </TabsList>

          {/* Open Tickets */}
          <TabsContent value="open">
            <div className="grid grid-cols-1 gap-4">
              {loading ? (
                <p className="text-center text-muted-foreground">Loading tickets...</p>
              ) : openTickets.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No open tickets</p>
                  </CardContent>
                </Card>
              ) : (
                openTickets.map((complaint) => {
                  const StatusIcon = getStatusIcon(complaint.status);
                  return (
                    <Card key={complaint.complaint_id} className="hover:shadow-md transition-shadow relative" data-testid={`ticket-${complaint.title}`}>
                      <div className="absolute top-4 right-4">
                        <Flag className="h-5 w-5 text-red-500" fill="currentColor" />
                      </div>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-8">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{complaint.title}</h3>
                              <Badge variant="destructive">Open</Badge>
                              <Badge variant="outline">{complaint.category}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">{complaint.description}</p>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>Ticket: #{complaint.complaint_id.substring(0, 8)}</span>
                              <span>•</span>
                              <span>From: {getFranchiseeName(complaint.franchisee_id)}</span>
                              <span>•</span>
                              <span>{new Date(complaint.submitted_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4 mt-6">
                            <Button size="sm" variant="outline" onClick={() => openViewDialog(complaint)}>
                              View
                            </Button>
                            {canManageComplaints && (
                              <>
                                <Button size="sm" onClick={() => openAssignDialog(complaint)}>
                                  <User className="h-4 w-4 mr-1" />
                                  Assign
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => openUpdateDialog(complaint)}>
                                  Update
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* In Progress Tickets */}
          <TabsContent value="progress">
            <div className="grid grid-cols-1 gap-4">
              {inProgressTickets.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No tickets in progress</p>
                  </CardContent>
                </Card>
              ) : (
                inProgressTickets.map((complaint) => (
                  <Card key={complaint.complaint_id} className="hover:shadow-md transition-shadow relative">
                    <div className="absolute top-4 right-4">
                      <Flag className="h-5 w-5 text-yellow-500" fill="currentColor" />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-8">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{complaint.title}</h3>
                            <Badge variant="secondary">In Progress</Badge>
                            <Badge variant="outline">{complaint.category}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{complaint.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">Assigned to:</span>
                            <div className="flex items-center gap-2 bg-primary/5 px-3 py-1 rounded-full">
                              <User className="h-3 w-3 text-primary" />
                              <span className="font-medium text-primary">{complaint.assigned_to_name || complaint.assigned_to || 'Unassigned'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4 mt-6">
                          <Button size="sm" variant="outline" onClick={() => openViewDialog(complaint)}>
                            View
                          </Button>
                          {(canManageComplaints || user.role === 'REGIONAL_COORDINATOR') && (
                            <Button size="sm" variant="secondary" onClick={() => openUpdateDialog(complaint)}>
                              Update
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Resolved Tickets */}
          <TabsContent value="resolved">
            <div className="grid grid-cols-1 gap-4">
              {resolvedTickets.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No resolved tickets yet</p>
                  </CardContent>
                </Card>
              ) : (
                resolvedTickets.map((complaint) => (
                  <Card key={complaint.complaint_id} className="hover:shadow-md transition-shadow relative opacity-80">
                    <div className="absolute top-4 right-4">
                      <Flag className="h-5 w-5 text-green-500" fill="currentColor" />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-8">
                          <div className="flex items-center gap-3 mb-2">
                            <CheckCircle className="h-5 w-5 text-green-500" />
                            <h3 className="font-semibold text-lg">{complaint.title}</h3>
                            <Badge className="bg-green-500">Resolved</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{complaint.resolution_notes}</p>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Resolved by: {complaint.resolved_by}</span>
                            <span>•</span>
                            <span>{new Date(complaint.resolved_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="mt-6">
                          <Button size="sm" variant="outline" onClick={() => openViewDialog(complaint)}>
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Update Dialog */}
        <Dialog open={updateDialog} onOpenChange={setUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Ticket Status</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Status*</Label>
                <Select value={updateData.status} onValueChange={(value) => setUpdateData({...updateData, status: value})} required>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Resolution Notes</Label>
                <Textarea rows={4} value={updateData.resolution_notes} onChange={(e) => setUpdateData({...updateData, resolution_notes: e.target.value})} data-testid="resolution-notes-input" placeholder="Add notes about the resolution or progress..." />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setUpdateDialog(false)}>Cancel</Button>
                <Button type="submit" data-testid="update-complaint-form-btn">Update Ticket</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-1">{selectedComplaint?.title}</p>
                <p className="text-xs text-muted-foreground">{selectedComplaint?.category} • {selectedComplaint?.priority} Priority</p>
              </div>
              
              <div className="space-y-2">
                <Label>Assign To*</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select user to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role === 'REGIONAL_COORDINATOR' || u.role === 'HQ_ADMIN').map(u => (
                      <SelectItem key={u.user_id} value={u.email}>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          <span>{u.full_name}</span>
                          <span className="text-xs text-muted-foreground">({u.role.replace('_', ' ')})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">This will change the ticket status to "In Progress"</p>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAssignDialog(false)}>Cancel</Button>
                <Button onClick={handleAssign} disabled={!assignedTo}>Assign Ticket</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Ticket Details
              </DialogTitle>
            </DialogHeader>
            {selectedComplaint && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Badge variant={getStatusColor(selectedComplaint.status)} className="text-sm">
                    {selectedComplaint.status}
                  </Badge>
                  <Badge variant="outline">{selectedComplaint.category}</Badge>
                  <Flag className={`h-4 w-4 ${getPriorityColor(selectedComplaint.priority)}`} fill="currentColor" />
                  <span className="text-sm text-muted-foreground">{selectedComplaint.priority} Priority</span>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-2">{selectedComplaint.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedComplaint.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg text-sm">
                  <div>
                    <p className="text-muted-foreground">Ticket ID</p>
                    <p className="font-medium">#{selectedComplaint.complaint_id.substring(0, 8)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Franchisee</p>
                    <p className="font-medium">{getFranchiseeName(selectedComplaint.franchisee_id)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Submitted By</p>
                    <p className="font-medium">{selectedComplaint.submitted_by}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Submitted On</p>
                    <p className="font-medium">{new Date(selectedComplaint.submitted_at).toLocaleString()}</p>
                  </div>
                  {selectedComplaint.assigned_to && (
                    <div>
                      <p className="text-muted-foreground">Assigned To</p>
                      <div className="flex items-center gap-2 mt-1">
                        <User className="h-3 w-3 text-primary" />
                        <p className="font-medium text-primary">{selectedComplaint.assigned_to_name || selectedComplaint.assigned_to}</p>
                      </div>
                    </div>
                  )}
                </div>

                {selectedComplaint.resolution_notes && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Resolution Notes:</p>
                    <p className="text-sm text-muted-foreground">{selectedComplaint.resolution_notes}</p>
                  </div>
                )}

                {selectedComplaint.status === 'Resolved' && (
                  <div className="border-t pt-4 flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span>Resolved by {selectedComplaint.resolved_by} on {new Date(selectedComplaint.resolved_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ComplaintManagement;
